'use client'

import React, { useMemo, useCallback } from 'react'
import { Container, Typography } from '..'
import StepperSelected from '../../assets/svg/stepperSelected'
import { StepperProps, StepperSize } from './type'
import { StepStatus } from './style'
import StyledStepItem from './StyledStepItem/StyledStepItem'
import StyledStepCircle from './StyledStepCircle/StyledStepCircle'
import StyledStepDivider from './StyledStepDivider/StyledStepDivider'
import StyledStepperContainer from './StyledStepperContainer/StyledStepperContainer'
import StyledStepContentContainer from './StyledStepContentContainer/StyledStepContentContainer'
import useTheme from '../../hooks/useTheme'
import { getConfig } from './config'

interface StepItemProps {
    step: string
    stepNumber: number
    status: StepStatus
    isLastStep: boolean
    isClickable: boolean
    size?: StepperSize
    onStepClick?: (step: number) => void
}

/**
 * StepItem component representing a single step in the stepper
 */
const StepItem: React.FC<StepItemProps> = React.memo(
    ({
        step,
        stepNumber,
        status,
        isLastStep,
        isClickable,
        size = 'md',
        onStepClick
    }) => {
        const theme = useTheme()
        const config = getConfig(size)

        const handleClick = useCallback(() => {
            if (isClickable && onStepClick) {
                onStepClick(stepNumber)
            }
        }, [isClickable, onStepClick, stepNumber])

        const titleColor =
            status === 'COMPLETED'
                ? 'link'
                : status === 'CURRENT'
                  ? 'primary'
                  : 'subheading'
        const titleColorCode =
            status === 'COMPLETED'
                ? theme.color.stepper.divider.completedBg
                : status === 'CURRENT'
                  ? theme.color.stepper.border.currentBorder
                  : theme.color.stepper.divider.upcomingBg

        return (
            <StyledStepItem
                key={`stepper_${stepNumber}`}
                status={status}
                isClickable={isClickable}
                isLastStep={isLastStep}
                size={size}
                onClick={handleClick}
                role={isClickable ? 'button' : undefined}
                aria-current={status === 'CURRENT' ? 'step' : undefined}
                title={isClickable ? `Go to ${step}` : undefined}
            >
                <StyledStepContentContainer size={size}>
                    <StyledStepCircle
                        status={status}
                        size={size}
                        aria-hidden='true'
                    >
                        {status === 'COMPLETED' ? (
                            <StepperSelected
                                fill={theme.color.stepper.bg.completedBg}
                            />
                        ) : (
                            <Typography
                                variant={config.typography.circle}
                                color={
                                    status === 'CURRENT'
                                        ? 'primary'
                                        : 'subheading'
                                }
                            >
                                {stepNumber}
                            </Typography>
                        )}
                    </StyledStepCircle>

                    <Container
                        width={config.titleContainer.width}
                        height={config.titleContainer.height}
                        className='o-hidden'
                    >
                        <Typography
                            variant={config.typography.title}
                            color={titleColor}
                            colorCode={titleColorCode}
                            className={size === 'sm' ? 'ta-c' : 'ml-1'}
                        >
                            {step}
                        </Typography>
                    </Container>
                </StyledStepContentContainer>

                {!isLastStep && (
                    <StyledStepDivider
                        status={status}
                        size={size}
                        aria-hidden='true'
                    />
                )}
            </StyledStepItem>
        )
    }
)

StepItem.displayName = 'StepItem'

/**
 * Stepper component for displaying a sequence of steps
 */
const Stepper: React.FC<StepperProps> = ({
    steps,
    currentStep,
    onStepClick,
    className,
    dataTestId,
    allStepsClickable = false,
    size = 'md'
}) => {
    // Memoize the processed steps to avoid unnecessary calculations
    const processedSteps = useMemo(() => {
        return steps.map((step, index) => {
            let status: StepStatus = 'UPCOMING'
            const stepNumber = index + 1

            if (stepNumber < currentStep) {
                status = 'COMPLETED'
            } else if (stepNumber === currentStep) {
                status = 'CURRENT'
            }

            const isLastStep = index === steps.length - 1
            // Determine if the step is clickable
            const isClickable =
                !!onStepClick && (allStepsClickable || status === 'COMPLETED')

            return {
                step,
                status,
                isLastStep,
                isClickable
            }
        })
    }, [steps, currentStep, onStepClick, allStepsClickable])

    return (
        <div
            className={className}
            role='navigation'
            aria-label='Progress'
            data-testid={dataTestId}
        >
            <StyledStepperContainer>
                {processedSteps.map(
                    ({ step, status, isLastStep, isClickable }, index) => (
                        <StepItem
                            key={`step-${index + 1}`}
                            stepNumber={index + 1}
                            step={step}
                            status={status}
                            isLastStep={isLastStep}
                            isClickable={isClickable}
                            size={size}
                            onStepClick={onStepClick}
                        />
                    )
                )}
            </StyledStepperContainer>
        </div>
    )
}

export default React.memo(Stepper)
